import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Format money helper supporting Shopify currency formatting
 * @param {number} cents 
 * @param {string} format 
 * @returns {string}
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const baseEur = (parseFloat(cents) || 0) / 100.0;

  const savedCountryCode = typeof localStorage !== 'undefined' ? localStorage.getItem('waseem_selected_country') : null;
  if (savedCountryCode && typeof window !== 'undefined' && window.WASEEM_COUNTRIES) {
    const country = window.WASEEM_COUNTRIES.find((c) => c.code === savedCountryCode);
    if (country) {
      if (country.currency === 'EUR' && country.rate === 1.0) {
        return `€${baseEur.toFixed(2).replace('.', ',')}`;
      }
      const converted = baseEur * country.rate;
      const cleanSymbol = (country.symbol || '').trim();
      if (['PKR', 'INR', 'JPY', 'KRW'].includes(country.currency)) {
        return `${cleanSymbol} ${Math.round(converted).toLocaleString('en-US')}`;
      }
      if (['TRY', 'NOK', 'SEK', 'DKK', 'PLN', 'ZAR', 'MXN', 'BRL', 'CNY', 'CHF'].includes(country.currency)) {
        return `${cleanSymbol} ${converted.toFixed(2).replace('.', ',')}`;
      }
      if (['USD', 'CAD', 'AUD', 'NZD', 'SGD', 'HKD'].includes(country.currency)) {
        return `${cleanSymbol}${converted.toFixed(2)}`;
      }
      if (country.currency === 'GBP') {
        return `£${converted.toFixed(2)}`;
      }
      return `${cleanSymbol} ${converted.toFixed(2)}`;
    }
  }

  return `€${baseEur.toFixed(2).replace('.', ',')}`;
}

/**
 * Get active language strings for bundle UI
 */
function getLocaleStrings() {
  const lang = (document.documentElement.lang || window.Shopify?.locale || 'nl').toLowerCase();
  if (lang.startsWith('fr')) {
    return {
      save: 'Économisez',
      bundleDiscount: 'Remise sur le pack',
      singleAdd: 'Ajouter au panier • ',
      bundleAdd: (count, price) => `Acheter le lot (${count} articles) • ${price}`,
      added: '✓ Ajouté au panier !',
      error: 'Erreur lors de l’ajout. Veuillez réessayer.',
    };
  }
  if (lang.startsWith('de')) {
    return {
      save: 'Spare',
      bundleDiscount: 'Bundle-Rabatt',
      singleAdd: 'In den Warenkorb • ',
      bundleAdd: (count, price) => `Set kaufen (${count} Artikel) • ${price}`,
      added: '✓ In den Warenkorb gelegt!',
      error: 'Fehler beim Hinzufügen. Bitte erneut versuchen.',
    };
  }
  if (lang.startsWith('en')) {
    return {
      save: 'Save',
      bundleDiscount: 'Bundle Discount',
      singleAdd: 'Add to Cart • ',
      bundleAdd: (count, price) => `Buy Bundle (${count} items) • ${price}`,
      added: '✓ Added to cart!',
      error: 'Error adding to cart. Please try again.',
    };
  }
  return {
    save: 'Bespaar',
    bundleDiscount: 'Bundelkorting',
    singleAdd: 'Voeg Toe Aan Winkelwagen • ',
    bundleAdd: (count, price) => `Koop Bundel (${count} items) • ${price}`,
    added: '✓ Toegevoegd aan winkelwagen!',
    error: 'Fout bij toevoegen. Probeer opnieuw.',
  };
}

/**
 * CustomProductBundle Web Component
 */
class CustomProductBundle extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#initEvents();
    this.#loadDiscoveryRecommendations();
    this.recalculate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #initEvents() {
    const { signal } = this.#abortController;

    // Listen to checkbox changes
    const checkboxes = this.querySelectorAll('.custom-bundle-checkbox');
    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => this.recalculate(), { signal });
    });

    // Listen to addon variant dropdown changes
    const variantSelects = this.querySelectorAll('.custom-bundle-variant-select');
    variantSelects.forEach((select) => {
      select.addEventListener('change', (e) => this.#handleVariantChange(e), { signal });
    });

    // Listen to Add Bundle Button Click
    const bundleBtn = this.querySelector('.custom-bundle-button');
    if (bundleBtn) {
      bundleBtn.addEventListener('click', (e) => this.#handleAddBundle(e), { signal });
    }

    // Listen to main product variant updates to sync main product price
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener(ThemeEvents.variantUpdate, (e) => this.#handleMainVariantUpdate(e), { signal });
      section.addEventListener(ThemeEvents.variantSelected, (e) => this.#handleMainVariantUpdate(e), { signal });
    }

    // Listen to global currency switch events
    window.addEventListener('waseem:currency:changed', () => {
      this.recalculate();
    }, { signal });
  }

  /**
   * Automatically load Complementary products configured in Shopify Search & Discovery App
   */
  async #loadDiscoveryRecommendations() {
    const mainId = this.dataset.mainProductId;
    if (!mainId || this.dataset.useDiscovery !== 'true') return;

    try {
      const discountPct = parseFloat(this.dataset.globalDiscount || '20');
      const preselect = this.dataset.preselect !== 'false';
      const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';

      const res = await fetch(`/recommendations/products.json?product_id=${mainId}&intent=complementary&limit=3`);
      if (!res.ok) return;
      const data = await res.json();
      const recProducts = (data && data.products) ? data.products.filter(p => String(p.id) !== String(mainId) && p.available !== false) : [];

      if (recProducts.length === 0) return;

      const itemsList = this.querySelector('.custom-bundle-items-list');
      if (!itemsList) return;

      // Remove existing non-main addon items
      const oldAddons = itemsList.querySelectorAll('.custom-bundle-item:not(.is-main)');
      oldAddons.forEach((el) => el.remove());

      recProducts.slice(0, 3).forEach((prod) => {
        const variant = prod.variants?.[0] || {};
        const origPrice = variant.price || prod.price || 0;
        const discAmt = Math.round((origPrice * discountPct) / 100);
        const bundlePrice = Math.max(0, origPrice - discAmt);
        const savings = origPrice - bundlePrice;
        const imgSrc = variant.featured_image?.src || prod.featured_image || (prod.images && prod.images[0]) || '';

        const itemEl = document.createElement('div');
        itemEl.className = `custom-bundle-item${preselect ? ' is-selected' : ''}`;
        itemEl.dataset.productId = String(prod.id);
        itemEl.dataset.variantId = String(variant.id || prod.id);
        itemEl.dataset.productTitle = prod.title;
        itemEl.dataset.originalPrice = String(origPrice);
        itemEl.dataset.bundlePrice = String(bundlePrice);
        itemEl.dataset.discountValue = String(discountPct);

        let variantHtml = '';
        if (prod.variants && prod.variants.length > 1) {
          const optionsHtml = prod.variants
            .map((v) => {
              const vOrig = v.price;
              const vDiscAmt = Math.round((vOrig * discountPct) / 100);
              const vBundle = vOrig - vDiscAmt;
              const vImg = v.featured_image?.src || imgSrc;
              return `<option value="${v.id}" data-price="${vOrig}" data-image="${vImg}">${v.title}</option>`;
            })
            .join('');
          variantHtml = `<select class="custom-bundle-variant-select" aria-label="Select variant for ${prod.title}">${optionsHtml}</select>`;
        } else if (variant.title && variant.title !== 'Default Title') {
          variantHtml = `<p class="custom-bundle-item__variant-text">${variant.title}</p>`;
        }

        itemEl.innerHTML = `
          <div class="custom-bundle-checkbox-wrapper">
            <input
              type="checkbox"
              class="custom-bundle-checkbox"
              id="bundle-addon-${prod.id}"
              ${preselect ? 'checked' : ''}
              aria-label="Add ${prod.title} to bundle"
            >
          </div>
          <div class="custom-bundle-item__image-wrap">
            ${imgSrc ? `<img src="${imgSrc}" alt="${prod.title}" class="custom-bundle-item__image" loading="lazy" width="60" height="60">` : ''}
          </div>
          <div class="custom-bundle-item__info">
            <div class="custom-bundle-item__title-row">
              <a href="${prod.url || '#'}" class="custom-bundle-item__title" target="_blank">
                ${prod.title}
              </a>
            </div>
            ${variantHtml}
          </div>
          <div class="custom-bundle-item__pricing">
            <span class="custom-bundle-item__bundle-price">${formatMoney(bundlePrice, moneyFormat)}</span>
            <span class="custom-bundle-item__original-price">${formatMoney(origPrice, moneyFormat)}</span>
            ${savings > 0 ? `<span class="custom-bundle-item__savings-pill">Bespaar ${formatMoney(savings, moneyFormat)}</span>` : ''}
          </div>
        `;

        itemsList.appendChild(itemEl);
      });

      // Re-bind events for newly created addon items
      const { signal } = this.#abortController;
      itemsList.querySelectorAll('.custom-bundle-checkbox').forEach((cb) => {
        cb.addEventListener('change', () => this.recalculate(), { signal });
      });
      itemsList.querySelectorAll('.custom-bundle-variant-select').forEach((select) => {
        select.addEventListener('change', (e) => this.#handleVariantChange(e), { signal });
      });

      this.recalculate();

      // Update currency if active
      if (typeof window.convertAllPrices === 'function' && typeof window.WASEEM_COUNTRIES !== 'undefined') {
        const saved = localStorage.getItem('waseem_selected_country');
        const country = window.WASEEM_COUNTRIES.find((c) => c.code === saved);
        if (country) window.convertAllPrices(country);
      }
    } catch (e) {
      console.warn('[Bundle] Shopify Discovery complementary loading note:', e);
    }
  }

  /**
   * Recalculates bundle totals and updates UI
   */
  recalculate() {
    const mainItem = this.querySelector('.custom-bundle-item.is-main');
    const addonItems = this.querySelectorAll('.custom-bundle-item:not(.is-main)');
    const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';

    let totalOriginal = 0;
    let totalBundle = 0;
    let selectedAddonCount = 0;

    // 1. Calculate & Format Main Product Row
    if (mainItem) {
      const mainPrice = parseInt(mainItem.dataset.price || '0', 10);
      totalOriginal += mainPrice;
      totalBundle += mainPrice;

      const mainPriceEl = mainItem.querySelector('.custom-bundle-item__bundle-price');
      if (mainPriceEl) {
        mainPriceEl.textContent = formatMoney(mainPrice, moneyFormat);
      }
    }

    // 2. Calculate & Format Selected Addons
    addonItems.forEach((item) => {
      const checkbox = item.querySelector('.custom-bundle-checkbox');
      const isChecked = checkbox && checkbox.checked;
      const originalPrice = parseInt(item.dataset.originalPrice || '0', 10);
      const bundlePrice = parseInt(item.dataset.bundlePrice || '0', 10);
      const savings = Math.max(0, originalPrice - bundlePrice);
      const loc = getLocaleStrings();

      // Update item row prices in the DOM to active currency
      const bundlePriceEl = item.querySelector('.custom-bundle-item__bundle-price');
      const origPriceEl = item.querySelector('.custom-bundle-item__original-price');
      const savingsPill = item.querySelector('.custom-bundle-item__savings-pill');

      if (bundlePriceEl && bundlePrice > 0) {
        bundlePriceEl.textContent = formatMoney(bundlePrice, moneyFormat);
      }
      if (origPriceEl && originalPrice > 0) {
        origPriceEl.textContent = formatMoney(originalPrice, moneyFormat);
      }
      if (savingsPill) {
        if (savings > 0) {
          savingsPill.textContent = `${loc.save} ${formatMoney(savings, moneyFormat)}`;
          savingsPill.style.display = '';
        } else {
          savingsPill.style.display = 'none';
        }
      }

      if (isChecked && bundlePrice > 0) {
        item.classList.add('is-selected');
        selectedAddonCount++;

        totalOriginal += originalPrice;
        totalBundle += bundlePrice;
      } else if (!isChecked) {
        item.classList.remove('is-selected');
      }
    });

    // 3. Format dropdown options inside chooser if present
    this.querySelectorAll('.custom-bundle-chooser__option').forEach((opt) => {
      const spOrig = parseInt(opt.dataset.price || '0', 10);
      const spBundle = parseInt(opt.dataset.bundlePrice || '0', 10);
      const spBundleEl = opt.querySelector('.custom-bundle-chooser__option-bundle-price');
      const spOrigEl = opt.querySelector('.custom-bundle-chooser__option-orig-price');
      if (spBundleEl && spBundle > 0) spBundleEl.textContent = formatMoney(spBundle, moneyFormat);
      if (spOrigEl && spOrig > 0) spOrigEl.textContent = formatMoney(spOrig, moneyFormat);
    });

    // 4. Calculate Savings
    const totalSavings = Math.max(0, totalOriginal - totalBundle);
    const totalItemsCount = (mainItem ? 1 : 0) + selectedAddonCount;
    const loc = getLocaleStrings();

    // 5. Update DOM Elements
    const totalPriceEl = this.querySelector('.custom-bundle-total-price');
    const totalOriginalEl = this.querySelector('.custom-bundle-total-original');
    const totalSavingsEl = this.querySelector('.custom-bundle-total-savings-badge');
    const bundleBtn = this.querySelector('.custom-bundle-button');
    const bundleBtnText = this.querySelector('.custom-bundle-button-text');

    if (totalPriceEl) {
      totalPriceEl.textContent = formatMoney(totalBundle, moneyFormat);
    }

    if (totalOriginalEl) {
      if (totalSavings > 0) {
        totalOriginalEl.textContent = formatMoney(totalOriginal, moneyFormat);
        totalOriginalEl.style.display = 'inline';
      } else {
        totalOriginalEl.style.display = 'none';
      }
    }

    if (totalSavingsEl) {
      if (totalSavings > 0) {
        const savingsPct = Math.round((totalSavings / totalOriginal) * 100);
        totalSavingsEl.innerHTML = `<span>${loc.save} ${formatMoney(totalSavings, moneyFormat)}</span><span class="custom-bundle-savings-pct">(${savingsPct}%)</span>`;
        totalSavingsEl.style.display = 'inline-flex';
      } else {
        totalSavingsEl.style.display = 'none';
      }
    }

    if (bundleBtn && bundleBtnText) {
      if (selectedAddonCount === 0) {
        bundleBtnText.textContent = `${loc.singleAdd}${formatMoney(totalBundle, moneyFormat)}`;
      } else {
        bundleBtnText.textContent = loc.bundleAdd(totalItemsCount, formatMoney(totalBundle, moneyFormat));
      }
    }
  }

  /**
   * Handle addon variant selection
   */
  #handleVariantChange(event) {
    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];
    const item = select.closest('.custom-bundle-item');
    if (!item || !selectedOption) return;

    const variantId = selectedOption.value;
    const originalPrice = parseInt(selectedOption.dataset.price || '0', 10);
    const discountType = this.dataset.discountType || 'percentage';
    const discountValue = parseFloat(item.dataset.discountValue || '20');

    let bundlePrice = originalPrice;
    if (discountType === 'percentage') {
      const discAmount = Math.round((originalPrice * discountValue) / 100);
      bundlePrice = originalPrice - discAmount;
    } else if (discountType === 'fixed_price' && item.dataset.customPrice) {
      bundlePrice = parseInt(item.dataset.customPrice, 10);
    } else if (discountType === 'amount_off') {
      bundlePrice = Math.max(0, originalPrice - discountValue);
    }

    const savings = Math.max(0, originalPrice - bundlePrice);

    // Update item datasets
    item.dataset.variantId = variantId;
    item.dataset.originalPrice = String(originalPrice);
    item.dataset.bundlePrice = String(bundlePrice);

    // Update item DOM display
    const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const bundlePriceEl = item.querySelector('.custom-bundle-item__bundle-price');
    const origPriceEl = item.querySelector('.custom-bundle-item__original-price');
    const savingsPill = item.querySelector('.custom-bundle-item__savings-pill');

    if (bundlePriceEl) bundlePriceEl.textContent = formatMoney(bundlePrice, moneyFormat);
    if (origPriceEl) origPriceEl.textContent = formatMoney(originalPrice, moneyFormat);
    if (savingsPill && savings > 0) {
      savingsPill.textContent = `Bespaar ${formatMoney(savings, moneyFormat)}`;
    }

    // Update variant image if available
    const variantImgSrc = selectedOption.dataset.image;
    const imgEl = item.querySelector('.custom-bundle-item__image');
    if (variantImgSrc && imgEl) {
      imgEl.src = variantImgSrc;
    }

    this.recalculate();
  }

  /**
   * Sync with main product variant changes
   */
  #handleMainVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    const mainItem = this.querySelector('.custom-bundle-item.is-main');
    if (!mainItem) return;

    mainItem.dataset.variantId = String(variant.id);
    mainItem.dataset.price = String(variant.price);

    const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const priceEl = mainItem.querySelector('.custom-bundle-item__bundle-price');
    const variantTextEl = mainItem.querySelector('.custom-bundle-item__variant-text');

    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);
    if (variantTextEl && variant.title && variant.title !== 'Default Title') {
      variantTextEl.textContent = variant.title;
    }

    if (variant.featured_image?.src) {
      const imgEl = mainItem.querySelector('.custom-bundle-item__image');
      if (imgEl) imgEl.src = variant.featured_image.src;
    }

    this.recalculate();
  }

  /**
   * Adds the bundle items (main product + selected add-ons) atomically to cart
   */
  async #handleAddBundle(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    const originalText = button.querySelector('.custom-bundle-button-text')?.textContent;
    const spinner = button.querySelector('.custom-bundle-button-spinner');
    if (spinner) spinner.style.display = 'block';

    try {
      const mainItem = this.querySelector('.custom-bundle-item.is-main');
      const addonItems = this.querySelectorAll('.custom-bundle-item:not(.is-main)');

      // Get main product form & quantity
      const productForm = document.querySelector('product-form-component');
      const qtyInput = productForm?.querySelector('input[name="quantity"]') || document.querySelector('input[name="quantity"]');
      const mainQty = parseInt(qtyInput?.value || '1', 10) || 1;

      const mainProductId = this.dataset.mainProductId || '';
      const mainProductTitle = this.dataset.mainProductTitle || 'Main Product';
      const bundleGroupId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

      const itemsToAdd = [];

      // 1. Add Main Product
      if (mainItem) {
        const mainVariantId = mainItem.dataset.variantId;
        if (mainVariantId) {
          itemsToAdd.push({
            id: parseInt(mainVariantId, 10),
            quantity: mainQty,
            properties: {
              _bundle_group_id: bundleGroupId,
              _bundle_role: 'main',
              _bundle_parent_id: String(mainProductId),
            },
          });
        }
      }

      // 2. Add Checked Addons
      addonItems.forEach((item) => {
        const checkbox = item.querySelector('.custom-bundle-checkbox');
        if (checkbox && checkbox.checked) {
          const addonVariantId = item.dataset.variantId;
          const addonTitle = item.dataset.productTitle || 'Add-On';
          const originalPrice = parseInt(item.dataset.originalPrice || '0', 10);
          const bundlePrice = parseInt(item.dataset.bundlePrice || '0', 10);
          const savings = Math.max(0, originalPrice - bundlePrice);
          const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';

          if (addonVariantId) {
            itemsToAdd.push({
              id: parseInt(addonVariantId, 10),
              quantity: mainQty,
              properties: {
                _bundle_group_id: bundleGroupId,
                _bundle_role: 'addon',
                _bundle_parent_id: String(mainProductId),
                _bundle_parent_title: mainProductTitle,
                _bundle_price: String(bundlePrice),
                _bundle_discount: String(savings),
                'Bundle Met': mainProductTitle,
                'Bundelkorting': savings > 0 ? `Bespaar ${formatMoney(savings, moneyFormat)}` : 'Bundelprijs',
              },
            });
          }
        }
      });

      if (itemsToAdd.length === 0) {
        button.classList.remove('is-loading');
        if (spinner) spinner.style.display = 'none';
        return;
      }

      // 3. Query Section IDs for Cart Drawer live refresh
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = new Set();
      cartItemsComponents.forEach((c) => {
        if (c.dataset.sectionId) sectionIds.add(c.dataset.sectionId);
      });

      const drawerSection = document.querySelector('cart-drawer-component')?.closest('.shopify-section');
      if (drawerSection?.id) {
        sectionIds.add(drawerSection.id.replace('shopify-section-', ''));
      }

      const payload = {
        items: itemsToAdd,
      };

      if (sectionIds.size > 0) {
        payload.sections = Array.from(sectionIds).join(',');
      }

      // 4. Send Atomic Cart Request
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status && result.status !== 200) {
        throw new Error(result.description || result.message || 'Cart add error');
      }

      // 5. Apply auto discount code if configured
      const autoDiscountCode = this.dataset.autoDiscountCode;
      if (autoDiscountCode && itemsToAdd.length > 1) {
        try {
          await fetch(`/discount/${encodeURIComponent(autoDiscountCode)}`);
        } catch (discErr) {
          console.warn('[Bundle] Auto discount apply note:', discErr);
        }
      }

      // 6. Dispatch Cart Add Event to automatically open Cart Drawer
      const fetchCartRes = await fetch('/cart.js');
      const updatedCart = await fetchCartRes.json();

      document.dispatchEvent(
        new CartAddEvent(updatedCart, String(itemsToAdd[0].id), {
          source: 'custom-product-bundle',
          itemCount: itemsToAdd.reduce((sum, it) => sum + it.quantity, 0),
          productId: mainProductId,
          sections: result.sections,
        })
      );

      // Trigger theme cart updates & drawer open
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, { detail: { cart: updatedCart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: updatedCart } }));

      // Success animation
      const buttonTextEl = button.querySelector('.custom-bundle-button-text');
      const loc = getLocaleStrings();
      if (buttonTextEl) {
        buttonTextEl.textContent = loc.added;
      }

      setTimeout(() => {
        if (buttonTextEl && originalText) {
          buttonTextEl.textContent = originalText;
        }
        button.classList.remove('is-loading');
        if (spinner) spinner.style.display = 'none';
      }, 1500);
    } catch (err) {
      console.error('[Bundle] Error adding bundle to cart:', err);
      button.classList.remove('is-loading');
      if (spinner) spinner.style.display = 'none';

      const buttonTextEl = button.querySelector('.custom-bundle-button-text');
      const loc = getLocaleStrings();
      if (buttonTextEl) {
        buttonTextEl.textContent = loc.error;
        setTimeout(() => {
          this.recalculate();
        }, 2000);
      }
    }
  }
}

if (!customElements.get('custom-product-bundle')) {
  customElements.define('custom-product-bundle', CustomProductBundle);
}
